// 004 (FR-002, FR-008, FR-049, FR-053; P-PROF-2): My children. Linked rows show
// "Class 5 · Section A · <School>" with Edit and Remove; unlinked rows show "Not linked to a school"
// with Link with school code (US6) and Remove. Add a child opens Add student on the last scanned or
// opened school when a pending join is kept (amendment 2026-10-08), else 003's code step.
import { useRouter } from "expo-router";
import { View } from "react-native";

import { addStudentHref } from "@/join/handoff";
import { pendingJoin } from "@/join/pendingJoin";
import { InlineMessage } from "@/ui/InlineMessage";
import { LinkButton } from "@/ui/LinkButton";
import { Text } from "@/ui/Text";

import { useRemoveChild } from "./children/useRemoveChild";
import { PROFILE_COPY } from "./copy";
import { Avatar, Card, SectionHead, useProfileStyles } from "./ui";
import { canAddChild, type ChildVM } from "./viewModels";

export function ChildrenSection({
  items,
  online,
}: {
  items: readonly ChildVM[];
  online: boolean;
}) {
  const styles = useProfileStyles();
  const router = useRouter();
  const remove = useRemoveChild();
  const canAdd = canAddChild(items.length);

  return (
    <Card testID="profile-children">
      <SectionHead
        title={PROFILE_COPY.children}
        subtitle={PROFILE_COPY.childrenSubtitle}
        action={
          canAdd ? (
            <LinkButton
              disabled={!online}
              onPress={() =>
                void pendingJoin
                  .read()
                  .then((p) =>
                    router.push(
                      p
                        ? addStudentHref(p.code, p.source)
                        : "/enter-school-code?mode=add",
                    ),
                  )
              }
            >
              {PROFILE_COPY.addChild}
            </LinkButton>
          ) : null
        }
      />
      {!canAdd ? <Text tone="muted">{PROFILE_COPY.childLimit}</Text> : null}
      {items.length === 0 ? (
        <Text tone="muted">{PROFILE_COPY.childrenEmpty}</Text>
      ) : null}
      {items.map((c) => (
        <View key={c.id} style={styles.row} testID={`child-${c.id}`}>
          <Avatar text={c.initial} />
          <View style={styles.rowText}>
            <Text weight="semiBold">{c.name}</Text>
            <Text tone="muted">
              {c.kind === "linked"
                ? PROFILE_COPY.childLine(c.classLabel, c.section, c.school.name)
                : PROFILE_COPY.notLinked}
            </Text>
            {c.hasOpenOrder ? (
              <Text tone="muted" size="sm">
                {PROFILE_COPY.openOrderRemove(c.name)}
              </Text>
            ) : null}
            {remove.error?.id === c.id && remove.error.text ? (
              <InlineMessage tone="error">{remove.error.text}</InlineMessage>
            ) : null}
          </View>
          <View style={styles.actions}>
            {c.kind === "linked" ? (
              <LinkButton
                accessibilityLabel={PROFILE_COPY.editChild(c.name)}
                disabled={!online}
                onPress={() => router.push(`/children/${c.id}`)}
              >
                Edit
              </LinkButton>
            ) : (
              <LinkButton
                accessibilityLabel={`${PROFILE_COPY.linkWithCode}: ${c.name}`}
                disabled={!online}
                onPress={() => router.push(`/children/${c.id}?link=1`)}
              >
                {PROFILE_COPY.linkWithCode}
              </LinkButton>
            )}
            <LinkButton
              accessibilityLabel={PROFILE_COPY.removeChild(c.name)}
              disabled={!online || c.hasOpenOrder}
              onPress={() => remove.ask({ id: c.id, name: c.name })}
            >
              {PROFILE_COPY.remove}
            </LinkButton>
          </View>
        </View>
      ))}
      {remove.dialog}
    </Card>
  );
}
